import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuthenticated } from "../auth/SessionProvider";
import { post, postFile } from "./client";
import { downloadBlob } from "./scores";
import type {
  ExamGroupId,
  PaperPreviewResponse,
  PaperPublishRequest,
  PaperPublishResponse,
  PapersListResponse,
  SetCurrentPaperResponse
} from "./types";

export function usePapers() {
  const { token } = useAuthenticated();
  return useQuery({
    queryKey: ["papers", token],
    queryFn: () => post<PapersListResponse>("/papers/list", {}, token)
  });
}

function useInvalidatePapers() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ["papers"] });
    void client.invalidateQueries({ queryKey: ["catalog"] });
  };
}

export function usePublishPaper() {
  const { token } = useAuthenticated();
  const invalidate = useInvalidatePapers();
  return useMutation({
    mutationFn: (input: PaperPublishRequest) => post<PaperPublishResponse>("/papers/publish", input, token),
    onSuccess: (result) => { if (result.ok && !result.dryRun) invalidate(); }
  });
}

export function usePreviewPaper() {
  const { token } = useAuthenticated();
  return useMutation({
    mutationFn: (input: { examKey: string; withAnswers: boolean }) => post<PaperPreviewResponse>("/papers/preview", input, token)
  });
}

export function useSetPaperOpensAt() {
  const { token } = useAuthenticated();
  const invalidate = useInvalidatePapers();
  return useMutation({
    mutationFn: (input: { examKey: string; opensAt: string }) => post<{ examKey: string; opensAt: string }>("/papers/set-opens-at", input, token),
    onSuccess: invalidate
  });
}

export function useSetCurrentPaper() {
  const { token } = useAuthenticated();
  const invalidate = useInvalidatePapers();
  return useMutation({
    mutationFn: (input: { groupId: ExamGroupId; examKey: string; confirm: boolean }) => post<SetCurrentPaperResponse>("/papers/set-current", input, token),
    onSuccess: invalidate
  });
}

export function useDeletePaper() {
  const { token } = useAuthenticated();
  const invalidate = useInvalidatePapers();
  return useMutation({
    mutationFn: (examKey: string) => post<{ examKey: string; deleted: boolean }>("/papers/delete", { examKey }, token),
    onSuccess: invalidate
  });
}

export function useDownloadPaperSource() {
  const { token } = useAuthenticated();
  return useMutation({
    mutationFn: async (input: { examKey: string; fallbackName: string }) => {
      const { blob, filename } = await postFile("/papers/source", { examKey: input.examKey }, token, input.fallbackName);
      downloadBlob(filename, blob);
      return filename;
    }
  });
}
